import { getMacroChannelLabel } from "../../../constants/MacroChannels";
import { NodeSubType } from "../../../models/nodeSubType";
import { ActionTypeBackend, FrontendNodeType } from "../macroNode.types";

/**
 * Scaffolds a default macro flow for newly created macros.
 * Creates one Response (Message) block per channel, plus one Ticket Action block,
 * all pre-wired so edges and handles render immediately.
 *
 * @param n1Config  – The N1 trigger node config (mutated in place to add channel edges)
 * @param channels  – Active channels from the macro (e.g. ["whatsapp", "email"])
 * @param startIndex – The next available node index (typically 1 for a fresh macro)
 * @returns { scaffoldedNodes, newIndex }
 */
export function scaffoldDefaultMacroFlow(
    n1Config: any,
    channels: string[],
    startIndex: number
): { scaffoldedNodes: { [key: string]: any }; newIndex: number } {
    const scaffoldedNodes: { [key: string]: any } = {};
    let idx = startIndex;

    // Create the single Ticket Action node ID upfront so Response nodes can point to it
    const ticketActionIndex = idx + channels.length;
    const ticketActionId = `N${ticketActionIndex + 1}`;

    // Ensure N1 has trigger structure
    if (!n1Config.trigger) n1Config.trigger = { conditions: [{ operator_values: {} }] };
    if (!n1Config.trigger.conditions) n1Config.trigger.conditions = [{ operator_values: {} }];
    if (!n1Config.trigger.conditions[0]) n1Config.trigger.conditions[0] = { operator_values: {} };
    if (!n1Config.trigger.conditions[0].operator_values) n1Config.trigger.conditions[0].operator_values = {};

    // --- Create one Response block per channel ---
    for (const channel of channels) {
        idx++;
        const responseId = `N${idx}`;
        const title = getMacroChannelLabel(channel) + " response";

        scaffoldedNodes[responseId] = {
            type: ActionTypeBackend.MESSAGE,
            subType: NodeSubType.RESPONSE_BLOCK,
            channel,
            automationChannel: channel,
            nodeId: responseId,
            nodeIndex: idx,
            next: ticketActionId,
            title,
            frontendConfig: {
                nodeData: {
                    type: FrontendNodeType.MESSAGE,
                    subType: NodeSubType.RESPONSE_BLOCK,
                    channel,
                    automationChannel: channel,
                    nodeId: responseId,
                    nodeIndex: idx,
                    name: title,
                    title,
                }
            }
        };

        // Wire N1's trigger channel → this response node
        n1Config.trigger.conditions[0].operator_values[channel] = {
            key: channel,
            value: responseId,
        };
    }

    // --- Create one Ticket Action block ---
    scaffoldedNodes[ticketActionId] = {
        type: ActionTypeBackend.MULTI_ACTION,
        subType: NodeSubType.MULTI_ACTION,
        nodeId: ticketActionId,
        nodeIndex: ticketActionIndex + 1,
        title: "Ticket actions",
        frontendConfig: {
            nodeData: {
                type: FrontendNodeType.ACTION,
                subType: NodeSubType.MULTI_ACTION,
                nodeId: ticketActionId,
                nodeIndex: ticketActionIndex + 1,
                name: "Ticket actions",
                title: "Ticket actions",
            }
        }
    };

    return {
        scaffoldedNodes,
        newIndex: ticketActionIndex + 1,
    };
}
